// Enquiries: messages from the Contact and Request-a-project forms, and the
// admin inbox that handles them. Backend: EnquiriesController (/api/enquiries).
import apiClient from '../api/apiClient';

const ENQUIRY_ENDPOINTS = {
  BASE: '/api/enquiries',
  STATUS: id => `/api/enquiries/${encodeURIComponent(id)}/status`,
  BY_ID: id => `/api/enquiries/${encodeURIComponent(id)}`,
};

export const ENQUIRY_TYPES = {
  CONTACT: 'contact',
  PROJECT: 'project',
};

export const ENQUIRY_STATUSES = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  CLOSED: 'closed',
};

/**
 * Sends a Contact or Request-a-project message (public, no sign-in needed).
 * @param {{type: 'contact'|'project', name: string, email: string, phone?: string,
 *   subject?: string, message: string, projectType?: string, budget?: string,
 *   timeline?: string, orderId?: string, website?: string}} enquiry
 *   `website` is the honeypot field: always send what the hidden input holds.
 * @returns {Promise<{id: string}>}
 */
export const submitEnquiry = async enquiry => {
  const response = await apiClient.post(ENQUIRY_ENDPOINTS.BASE, enquiry);
  return response.data?.data;
};

/**
 * Admin inbox, newest first.
 * @param {{status?: string, type?: string, searchTerm?: string, currentPage?: number, itemsPerPage?: number}} filters
 * @returns {Promise<{items: object[], totalCount: number, currentPage: number, itemsPerPage: number, totalPages: number}>}
 */
export const getEnquiries = async (filters = {}) => {
  const response = await apiClient.get(ENQUIRY_ENDPOINTS.BASE, {
    params: {
      Status: filters.status || undefined,
      Type: filters.type || undefined,
      SearchTerm: filters.searchTerm || undefined,
      CurrentPage: filters.currentPage || 1,
      ItemsPerPage: filters.itemsPerPage || 20,
    },
  });
  return response.data?.data;
};

/**
 * Admin: move an enquiry to new / in_progress / closed.
 * @param {string} id
 * @param {'new'|'in_progress'|'closed'} status
 * @param {string} [note] Leave undefined to keep the current note; '' clears it.
 * @returns {Promise<object>} The updated enquiry.
 */
export const updateEnquiryStatus = async (id, status, note) => {
  const response = await apiClient.patch(ENQUIRY_ENDPOINTS.STATUS(id), {
    status,
    note,
  });
  return response.data?.data;
};

/** Admin: delete an enquiry. */
export const deleteEnquiry = async id => {
  const response = await apiClient.delete(ENQUIRY_ENDPOINTS.BY_ID(id));
  return response.data;
};

const enquiryService = {
  submitEnquiry,
  getEnquiries,
  updateEnquiryStatus,
  deleteEnquiry,
};

export default enquiryService;
